import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable, FilterBar, type FilterSpec } from "@/components/data";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { applyFilters, parseFilters } from "@/lib/filters";

import { mergeIssues } from "./feed_issues";
import { FeedIssuesNote } from "./FeedIssuesNote";
import { countryName, humanDate, messageFor, money, pct } from "./format";
import { keepValue } from "./LogsToolbar";
import { signedMoney } from "./OrderChart";
import {
  DEFAULT_DIR, EXCLUSION_LABEL, ORDER_KINDS, ORDERS_COPY, ORDERS_PAGE_SIZE, ordersNotApplied, ordersQuery, ordersSummaryQuery, paramsFrom, placeValues, scopeOf,
  searchForOrderFilters, searchForOrderKind, searchForPeriod, searchForSort,
  SORT_LABEL, STORE_SHORT, STORES, storeName, type OrderRow, type OrdersParams, type OrdersSort, type OrdersSummary, type Store,
} from "./orders";
import { Lines, OrderSheet } from "./OrderSheet";
import { PeriodPicker } from "./PeriodPicker";
import { RetryAlert } from "./WeeklyMovesSection";

const COUNT = new Intl.NumberFormat("en-GB");
const count = (n: number) => COUNT.format(n);
const SORTABLE: Record<string, OrdersSort> = { store: "store", date: "date", profit: "profit" };

/**
 * The Orders pane (R2). Two reads: the summary for the tiles, store cards and exclusions line (the module's filters, the kind
 * of order, the period and the refunds filter; never the show band) and one server page of orders (every filter, sort,
 * direction, page). Both live in the URL, so a filtered view is a link. The browser does no arithmetic on money: every figure
 * is the API's string. C1 v1.36: the module's six filters in the Price Run's order and words, the kind of order as a segment,
 * and Retry on a read that failed (the Overview's alert; reading again changes nothing).
 */
export function OrdersPane({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const go = (next: Record<string, unknown>) => void navigate({ to: ".", search: next, replace: true });
  // Taken once per mount: a pane left open across midnight or the month turn keeps its period and its query key.
  const [today] = useState(() => new Date());
  const params = paramsFrom(search, today);
  const summary = useQuery(ordersSummaryQuery(params));
  const page = useQuery(ordersQuery(params));
  const openOrder = typeof search.pp_order === "string" ? search.pp_order : null;

  const heading = (
    <div>
      <h2 className="text-lg font-semibold text-ks-ink">{ORDERS_COPY.title}</h2>
      <p className="text-sm text-ks-grey-1">{summary.data?.window.from && summary.data.window.to ? ORDERS_COPY.note(humanDate(summary.data.window.from), humanDate(summary.data.window.to)) : ORDERS_COPY.noteNoWindow}</p>
    </div>
  );
  if (summary.isPending) {
    return <div className="flex flex-col gap-3">{heading}<Card><CardContent className="flex flex-col gap-2 py-4">{[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-7 w-full" />)}</CardContent></Card></div>;
  }
  if (summary.error) {
    return <div className="flex flex-col gap-3">{heading}<RetryAlert message={messageFor(summary.error, areaName)} onRetry={() => void summary.refetch()} /></div>;
  }
  const s = summary.data;
  if (s.book.from === null) {
    return (
      <div className="flex flex-col gap-3">{heading}
        <Card><CardContent className="py-10 text-center"><p className="text-lg font-semibold text-ks-ink">{ORDERS_COPY.emptyTitle}</p><p className="text-ks-grey-1">{ORDERS_COPY.emptyBody}</p></CardContent></Card>
      </div>
    );
  }

  // The module's six filters, each with the options this book holds (every order is a Shopify order) and the URL's value kept:
  // a value carried from the Price Run or the Logs that no order has is shown, and the table says no order matches.
  const values = { ...parseFilters(search), ...placeValues(search, params) };
  const F = ORDERS_COPY.filters;
  const choice = (name: string, label: string, options: Array<{ value: string; label: string }>): FilterSpec => ({ name, label, kind: "choice", anyLabel: "All", options: keepValue(options, values[name]) });
  const filterSpecs: FilterSpec[] = [
    choice("product", F.product, (s.products ?? []).map((p) => ({ value: p, label: p }))),
    choice("variant", F.variant, (s.variants ?? []).map((v) => ({ value: v, label: v }))),
    choice("channel", F.channel, [{ value: ORDERS_COPY.channel, label: ORDERS_COPY.channel }]),
    choice("store", F.store, STORES.map((k) => ({ value: storeName(k), label: storeName(k) }))),
    choice("location", F.location, s.countries.map((c) => ({ value: c, label: c }))),
    choice("marketplace", F.marketplace, s.countries.map((c) => ({ value: countryName(c), label: countryName(c) }))),
    { name: "show", label: ORDERS_COPY.filters.show, kind: "choice", anyLabel: ORDERS_COPY.showLabel.all, options: (["costed", "losses", "excluded"] as const).map((k) => ({ value: k, label: ORDERS_COPY.showLabel[k] })) },
    { name: "refunds", label: ORDERS_COPY.filters.refunds, kind: "choice", anyLabel: ORDERS_COPY.refundsLabel.all, options: [{ value: "exclude", label: ORDERS_COPY.refundsLabel.exclude }] },
  ];
  const ignored = ordersNotApplied(params, s.filters);
  const total = page.data?.page.total ?? 0;
  const sortText = SORT_LABEL[params.sort] + (params.dir ? (params.sort === "profit" ? (params.dir === "asc" ? ", lowest first" : ", highest first") : params.sort === "date" ? (params.dir === "desc" ? ", newest first" : ", oldest first") : (params.dir === "asc" ? ", A to Z" : ", Z to A")) : "");
  const rows = page.data?.rows ?? [];
  const open = rows.find((r) => `${r.store}|${r.order_no}` === openOrder) ?? null;

  const columns: ColumnDef<OrderRow, unknown>[] = [
    { id: "order", header: ORDERS_COPY.columns[0], cell: ({ row: r }) => <OrderCell order={r.original} /> },
    { id: "store", header: ORDERS_COPY.columns[1], meta: { sortable: true }, cell: ({ row: r }) => STORE_SHORT[r.original.store as Store] },
    { id: "date", header: ORDERS_COPY.columns[2], meta: { sortable: true }, cell: ({ row: r }) => humanDate(r.original.order_date) },
    { id: "lines", header: ORDERS_COPY.columns[3], cell: ({ row: r }) => <Lines order={r.original} /> },
    { id: "ship_to", header: ORDERS_COPY.columns[4], cell: ({ row: r }) => countryName(r.original.ship_country) },
    { id: "revenue", header: ORDERS_COPY.columns[5], meta: { align: "right" }, cell: ({ row: r }) => <RevenueCell order={r.original} /> },
    { id: "shipping", header: ORDERS_COPY.columns[6], meta: { align: "right" }, cell: ({ row: r }) => r.original.customer_paid_shipping === "0.00" ? <span className="text-ks-grey-1">{ORDERS_COPY.free}</span> : money(r.original.customer_paid_shipping, r.original.currency) },
    { id: "cogs", header: ORDERS_COPY.columns[7], meta: { align: "right" }, cell: ({ row: r }) => <CogsCell order={r.original} /> },
    { id: "fees", header: ORDERS_COPY.columns[8], meta: { align: "right" }, cell: ({ row: r }) => <FeesCell order={r.original} /> },
    { id: "profit", header: ORDERS_COPY.columns[9], meta: { align: "right", sortable: true }, cell: ({ row: r }) => <ProfitCell order={r.original} /> },
  ];

  return (
    <div className="flex flex-col gap-3">
      {heading}
      <div className="flex flex-wrap items-center gap-3">
        <PeriodPicker from={params.from} to={params.to} book={s.book} today={today} onChange={(from, to) => go(searchForPeriod(search, from, to, today))} />
        <div role="group" aria-label={ORDERS_COPY.orderKind.label} className="flex gap-1">
          {ORDER_KINDS.map((k) => (
            <Button key={k} size="sm" data-nonmutating variant={params.orders === k ? "default" : "outline"} aria-pressed={params.orders === k}
              onClick={() => go(searchForOrderKind(search, k))}>{ORDERS_COPY.orderKind[k]}</Button>
          ))}
        </div>
      </div>
      <FilterBar layout="inline" filters={filterSpecs} values={values} onChange={(next) => go(searchForOrderFilters(applyFilters(search, next)))}
        trailing={page.data ? ORDERS_COPY.trailing(count(total), sortText) : undefined} />
      {ignored.length > 0 && <Alert role="note"><AlertDescription>{ORDERS_COPY.olderApi(ignored)}</AlertDescription></Alert>}
      {/* C1 v1.64 (C2 v1.62): the order feeds' open issues, as the Price Run names its own. */}
      <FeedIssuesNote issues={mergeIssues(s.feed_issues, page.data?.feed_issues)} />
      <Tiles s={s} params={params} />
      <StoreCards s={s} />
      {s.orders - s.costed > 0 && (
        <Alert role="status"><AlertDescription>
          {ORDERS_COPY.exclusions(count(s.orders - s.costed), count(s.excluded.incomplete), count(s.excluded.no_cogs), count(s.excluded.cancelled), s.excluded.currency_mismatch > 0 ? count(s.excluded.currency_mismatch) : null)}
        </AlertDescription></Alert>
      )}
      {page.error ? <RetryAlert message={messageFor(page.error, areaName)} onRetry={() => void page.refetch()} /> : (
        <DataTable
          columns={columns}
          rows={rows}
          pending={page.isPending}
          page={{ pageIndex: params.page, pageSize: ORDERS_PAGE_SIZE, rowCount: total }}
          onPageChange={(next) => go({ ...search, pp_opage: next || undefined })}
          sort={params.sort in SORTABLE ? { id: params.sort, direction: params.dir ?? DEFAULT_DIR[params.sort] ?? "asc" } : undefined}
          onSort={(id) => { const column = SORTABLE[id]; if (column) go(searchForSort(search, params, column)); }}
          caption={ORDERS_COPY.title}
          emptyMessage={ORDERS_COPY.noMatch}
          rowClassName={(r) => (r.loss_flag ? "bg-ks-warn/5" : undefined)}
          onRowClick={(r) => go({ ...search, pp_order: `${r.store}|${r.order_no}` })}
        />
      )}
      <p className="text-xs text-ks-grey-1">{ORDERS_COPY.footnote}</p>
      <OrderSheet order={open} onClose={() => go({ ...search, pp_order: undefined })} />
    </div>
  );
}

function Tiles({ s, params }: { s: OrdersSummary; params: OrdersParams }) {
  const period = params.wholeBook ? ORDERS_COPY.tiles.wholeBook : `${params.from ? humanDate(params.from) : ORDERS_COPY.period.start} ${ORDERS_COPY.period.to} ${params.to ? humanDate(params.to) : ORDERS_COPY.period.today}`;
  const scope = scopeOf(period, params);
  const tile = (label: string, value: string, sub: string, tone = "text-ks-ink") => (
    <Card><CardContent className="py-3"><div className="text-[10px] font-semibold uppercase tracking-wide text-ks-grey-1">{label}</div><div className={`text-2xl font-semibold tabular-nums ${tone}`}>{value}</div><div className="text-sm text-ks-grey-1">{sub}</div></CardContent></Card>
  );
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" role="group" aria-label="Period summary">
      {tile(ORDERS_COPY.tiles.orders, count(s.orders), scope)}
      {tile(ORDERS_COPY.tiles.profitable, count(s.profitable), s.profitable_pct == null ? ORDERS_COPY.tiles.noCosted : ORDERS_COPY.tiles.share(s.profitable_pct), "text-ks-pass")}
      {tile(ORDERS_COPY.tiles.losses, count(s.losses), s.losses_pct == null ? ORDERS_COPY.tiles.noCosted : ORDERS_COPY.tiles.share(s.losses_pct), "text-ks-warn")}
      {tile(ORDERS_COPY.tiles.excluded, count(s.orders - s.costed), ORDERS_COPY.tiles.excludedSub)}
    </div>
  );
}

function StoreCards({ s }: { s: OrdersSummary }) {
  if (s.by_store.length === 0) return null;
  return (
    <div className="grid gap-3 md:grid-cols-3" role="group" aria-label="Stores">
      {s.by_store.map((b) => (
        <Card key={b.store}><CardContent className="py-3">
          <div className="font-semibold text-ks-ink">{storeName(b.store)}</div>
          <div className="text-sm text-ks-grey-1">{ORDERS_COPY.storeCard.counts(count(b.orders), count(b.costed), count(b.losses))}</div>
          <div className="mt-2 flex justify-between border-t border-dashed border-ks-grey-4 pt-1 text-sm"><span>{ORDERS_COPY.storeCard.revenue}</span><b className="tabular-nums">{b.net_revenue_ex_vat == null ? ORDERS_COPY.none : money(b.net_revenue_ex_vat, b.currency)}</b></div>
          <div className="flex justify-between border-t border-dashed border-ks-grey-4 pt-1 text-sm"><span>{ORDERS_COPY.storeCard.profit}</span><b className={`tabular-nums ${b.profit == null ? "" : b.profit.startsWith("-") ? "text-ks-warn" : "text-ks-pass"}`}>{b.profit == null ? ORDERS_COPY.none : money(b.profit, b.currency)}</b></div>
          <div className="text-xs text-ks-grey-1">{b.costed > 0 ? ORDERS_COPY.storeCard.sub(count(b.costed)) : ORDERS_COPY.storeCard.allExcluded}</div>
        </CardContent></Card>
      ))}
    </div>
  );
}

function OrderCell({ order }: { order: OrderRow }) {
  return (
    <div>
      <b className="text-ks-ink">{order.order_no}</b>
      {order.subscription && order.subscription !== "none" && <div className="mt-1"><Badge variant="outline">{ORDERS_COPY.subscriptionTag[order.subscription]}</Badge></div>}
      {order.exclusion_reason !== "none" && <div className="mt-1"><Badge variant={order.exclusion_reason === "cancelled" ? "outline" : "secondary"} className={order.exclusion_reason === "cancelled" ? undefined : "text-ks-warn"}>{EXCLUSION_LABEL[order.exclusion_reason]}</Badge></div>}
    </div>
  );
}
function RevenueCell({ order }: { order: OrderRow }) {
  return (
    <>
      <span className="tabular-nums">{money(order.revenue_ex_vat, order.currency)}</span>
      {order.refund_state !== "none" && <div className="text-xs text-ks-grey-1">{ORDERS_COPY.refund(money(order.refund_amount, order.currency), order.refund_state)}</div>}
    </>
  );
}
function CogsCell({ order }: { order: OrderRow }) {
  if (order.cogs == null) return <span className="text-ks-grey-1">{ORDERS_COPY.none}</span>;
  return (
    <>
      <span className="tabular-nums">−{money(order.cogs, order.currency)}</span>
      <div className="text-xs text-ks-grey-1">{order.cogs_production_key}{order.cogs_flag && order.cogs_flag !== "BILLED" && <> <Badge variant="outline" className="border-ks-warn text-ks-warn">{ORDERS_COPY.costDatedAfterOrder}</Badge></>}</div>
    </>
  );
}
function FeesCell({ order }: { order: OrderRow }) {
  const fc = order.fee_components;
  if (order.fulfilment_fee_total == null) return <span className="text-ks-grey-1">{order.exclusion_reason === "cancelled" ? ORDERS_COPY.notComputed : ORDERS_COPY.noBilledLines}</span>;
  return (
    <>
      <span className="tabular-nums">−{money(order.fulfilment_fee_total, order.currency)}</span>
      <div className="text-xs text-ks-grey-1">{ORDERS_COPY.fees(money(fc.pick_pack, order.currency), money(fc.packaging, order.currency), money(fc.courier, order.currency))}</div>
    </>
  );
}
function ProfitCell({ order }: { order: OrderRow }) {
  if (order.profit == null) return <span className="text-ks-grey-1">{ORDERS_COPY.na}</span>;
  return (
    <>
      <span className={`font-semibold tabular-nums ${order.loss_flag ? "text-ks-warn" : "text-ks-pass"}`}>{signedMoney(order.profit, order.currency)}</span>
      <div className="text-xs text-ks-grey-1">{ORDERS_COPY.ofRevenue(pct(order.profit_pct))}{order.loss_flag && <> <Badge variant="destructive">{ORDERS_COPY.loss}</Badge></>}</div>
    </>
  );
}
